import axios from "axios";
import i18next from "i18next";
import { DEFAULT_CURRENCY, SLUG } from "../utils/global_const";
import qs from "qs";

const defaultCurrency = DEFAULT_CURRENCY;

export default class APIService {
  static URL =
    window.location.protocol === "https:"
      ? "https://api.wholesale.snusco.com:444/"
      : "http://localhost:8001/";

  static headers() {
    const authTokens = localStorage.getItem("authTokens");
    const accessToken = authTokens ? JSON.parse(authTokens).access : null;

    const headers = {
      "Accept-Language": i18next.language.toLowerCase(),
      Currency: localStorage.getItem("currency") || defaultCurrency,
      "Content-Type": "application/json",
      "x-domain": SLUG,
    };

    if (accessToken) {
      headers["Authorization"] = `Bearer ${accessToken}`;
    }

    return headers;
  }

  // ✅ 1. Lista javnih proizvoda (24 po strani)
  static async GetPublicProducts(page = 1, filters = {}) {
    const response = await axios.get(`${this.URL}api/public/products/`, {
      headers: this.headers(),
      params: {
        page,
        ...filters,
      },
      paramsSerializer: (params) => {
        return qs.stringify(params, { arrayFormat: "repeat" });
      },
    });
    return response.data;
  }

  // ✅ 2. Lista brendova
  static async GetPublicBrands() {
    const response = await axios.get(`${this.URL}api/public/brands/`, {
      headers: this.headers(),
    });
    return response.data;
  }

  // ✅ 3. Proizvodi za određeni brend + filteri i sortiranje
  static async GetProductsByBrand(brandSlug, page = 1, filters = {}) {
    const response = await axios.get(
      `${this.URL}api/public/brand/${brandSlug}/products/`,
      {
        headers: this.headers(),
        params: {
          page,
          ...filters,
        },
        paramsSerializer: (params) => {
          return qs.stringify(params, { arrayFormat: "repeat" });
        },
      }
    );
    return response.data;
  }

  static async GetProductBySlug(slug) {
    const response = await axios.get(
      `${this.URL}api/public/products/${slug}/`,
      {
        headers: this.headers(),
      }
    );
    return response.data;
  }

  static async GetRecommendedProductsBySlug(slug) {
    const response = await axios.get(
      `${this.URL}api/public/products/${slug}/recommended/`,
      {
        headers: this.headers(),
      }
    );
    return response.data;
  }

  static async GetFeaturedProducts(page = 1) {
    const response = await axios.get(
      `${this.URL}api/public/products/featured/?page=${page}`,
      {
        headers: this.headers(),
      }
    );
    return response.data;
  }
  static async GetNewArrivalsProducts(page = 1) {
    const response = await axios.get(
      `${this.URL}api/public/products/new-arrivals/?page=${page}`,
      {
        headers: this.headers(),
      }
    );
    return response.data;
  }

  static async SearchProducts(query) {
    try {
      const response = await axios.get(`${this.URL}api/products/search/`, {
        params: {
          search: query,
        },
        headers: this.headers(),
      });
      return response.data; // Očekuje se da će ovaj poziv vratiti listu proizvoda koji odgovaraju kriterijumima pretrage
    } catch (error) {
      throw error;
    }
  }

  // ✅ 4. Sve standardne cene po brendu
  static async GetStandardBrandPrices() {
    const response = await axios.get(`${this.URL}api/public/brand-prices/`, {
      headers: this.headers(),
    });
    return response.data;
  }

  //USER
  static async VerifyCode(code, accessToken) {
    try {
      const response = await axios.post(
        `${this.URL}api/auth/verify-code/`,
        { code: code },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`, // Koristite access token za autorizaciju
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }
  static async ResendVerification(email) {
    const language = i18next.language.toLowerCase();
    try {
      const response = await axios.post(
        `${this.URL}api/auth/resend-verification-code/`,
        { email: email },
        {
          headers: {
            "Content-Type": "application/json",
            "Accept-Language": language,
          },
        }
      );
      return response.data; // Očekuje se da backend vraća poruku o uspešnosti
    } catch (error) {
      throw error;
    }
  }

  static async GoogleLogin(token) {
    try {
      const response = await axios.post(`${this.URL}api/auth/google-login/`, {
        token: token,
      });

      // Ako je odgovor uspešan, axios će automatski parsirati JSON, tako da možete direktno vratiti data
      return response.data;
    } catch (error) {
      console.error("Greška prilikom autentifikacije sa Google: ", error);
      throw error;
    }
  }
  static async Login(email, password) {
    try {
      const response = await axios.post(`${this.URL}api/auth/login/`, {
        email: email,
        password: password,
      });
      return response.data; // Očekuje se da backend vraća objekat sa access i refresh tokenima
    } catch (error) {
      throw error;
    }
  }
  static async Register(userData) {
    try {
      const response = await axios.post(
        `${this.URL}api/auth/register/`,
        userData
      );
      return response.data; // Očekuje se da backend vraća token-e ili neki drugi odgovor
    } catch (error) {
      throw error;
    }
  }

  static async RefreshToken(refreshToken) {
    try {
      const response = await axios.post(`${this.URL}api/auth/refresh/`, {
        refresh: refreshToken,
      });
      return response.data; // Očekuje se da backend vraća novi access token
    } catch (error) {
      throw error;
    }
  }

  static async getUserProfile() {
    const cacheKey = "userProfileData";
    // Proverite da li su podaci već cache-irani
    const cachedData = localStorage.getItem(cacheKey);
    if (cachedData) {
      //return JSON.parse(cachedData); // Vraćanje cache-iranih podataka
    }
    try {
      const response = await axios.get(`${this.URL}api/auth/user/profile/`, {
        headers: this.headers(),
      });

      // Cache-iranje dobijenih podataka
      localStorage.setItem(cacheKey, JSON.stringify(response.data));
      return response.data; // Vraćanje dobijenih podataka
    } catch (error) {
      throw error;
    }
  }

  // 🔵 Dodavanje nove adrese
  static async createAddress(addressData, accessToken) {
    try {
      const response = await axios.post(
        `${this.URL}api/auth/user/addresses/`,
        addressData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // 🔵 Ažuriranje postojeće adrese
  static async updateAddress(addressId, addressData, accessToken) {
    try {
      const response = await axios.put(
        `${this.URL}api/auth/user/addresses/${addressId}/`,
        addressData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // 🔵 Brisanje adrese
  static async deleteAddress(addressId, accessToken) {
    try {
      const response = await axios.delete(
        `${this.URL}api/auth/user/addresses/${addressId}/`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async createOrder(orderData) {
    try {
      const response = await axios.post(
        `${this.URL}api/orders/create/`,
        orderData,
        {
          headers: this.headers(),
        }
      );
      return response.data; // Očekuje se da backend vraća ažuriranu adresu
    } catch (error) {
      throw error;
    }
  }
}
